import { useRef, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EnvelopeOpen, Plus, Trash, ArrowUp, ArrowDown, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const DEFAULTS = {
  enabled: false,
  subject: "Welcome to Aurora, {first_name}!",
  greeting: "Hi {first_name},",
  body: "",
  signature: "— The Aurora People Team",
  logo_url: null,
  links: [],
  email_domain: "auroraenergy.co",
  send_hour_local: 7,
};

export function WelcomeEmailPanel({ settings, setSettings, isAdmin }) {
  const we = settings.welcome_email || DEFAULTS;
  const fileRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const patch = (changes) =>
    setSettings((s) => ({ ...s, welcome_email: { ...(s.welcome_email || DEFAULTS), ...changes } }));

  const patchLink = (idx, changes) => {
    const next = [...(we.links || [])];
    next[idx] = { ...next[idx], ...changes };
    patch({ links: next });
  };
  const moveLink = (idx, delta) => {
    const next = [...(we.links || [])];
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    patch({ links: next });
  };
  const removeLink = (idx) => {
    patch({ links: (we.links || []).filter((_, i) => i !== idx) });
  };
  const addLink = () => {
    patch({ links: [...(we.links || []), { title: "", url: "" }] });
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("Logo must be 1 MB or smaller");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post("/settings/welcome-email/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      patch({ logo_url: r.data.logo_url });
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clearLogo = async () => {
    try {
      await api.delete("/settings/welcome-email/logo");
      patch({ logo_url: null });
      toast.success("Logo removed");
    } catch {
      toast.error("Could not remove logo");
    }
  };

  const openPreview = async () => {
    // Save current state so preview reflects what admin is editing.
    try {
      await api.patch("/settings", { welcome_email: we });
    } catch {
      // Non-fatal — preview reads from server anyway. Just continue.
    }
    try {
      const r = await api.post("/settings/welcome-email/preview");
      setPreview(r.data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not render preview");
    }
  };

  return (
    <div className="hairline rounded-lg p-4 bg-white space-y-4" data-testid="settings-welcome-email-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <EnvelopeOpen size={18} weight="duotone" className="text-[#4f4c90]" />
          <div>
            <h3 className="font-heading font-semibold text-lg">New starter welcome email</h3>
            <p className="text-xs text-slate-500">
              Sent to every new starter on their first day, Cc'd to their line manager.
              Edit the copy, link list and logo here — changes apply on the next morning's run.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            data-testid="welcome-enabled"
            checked={!!we.enabled}
            onCheckedChange={(v) => isAdmin && patch({ enabled: v })}
            disabled={!isAdmin}
          />
          <span className="text-xs text-slate-500">{we.enabled ? "On" : "Paused"}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Email domain</Label>
          <Input
            data-testid="welcome-domain"
            value={we.email_domain || ""}
            onChange={(e) => patch({ email_domain: e.target.value })}
            placeholder="auroraenergy.co"
            disabled={!isAdmin}
            className="bg-white border-slate-200"
          />
          <div className="text-[11px] text-slate-500 mt-1">
            Starter address is built as <code>firstname.lastname@{we.email_domain || "domain"}</code>.
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Send hour (local)</Label>
          <Input
            data-testid="welcome-send-hour"
            type="number" min={0} max={23}
            value={we.send_hour_local ?? 7}
            onChange={(e) => patch({ send_hour_local: Number(e.target.value) })}
            disabled={!isAdmin}
            className="bg-white border-slate-200"
          />
          <div className="text-[11px] text-slate-500 mt-1">
            Workspace timezone. The daily sweep won't fire before this hour.
          </div>
        </div>
      </div>

      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Subject</Label>
        <Input
          data-testid="welcome-subject"
          value={we.subject || ""}
          onChange={(e) => patch({ subject: e.target.value })}
          placeholder="Welcome to Aurora, {first_name}!"
          disabled={!isAdmin}
          className="bg-white border-slate-200"
        />
        <div className="text-[11px] text-slate-500 mt-1">Use <code>{"{first_name}"}</code> for personalisation.</div>
      </div>

      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Greeting</Label>
        <Input
          data-testid="welcome-greeting"
          value={we.greeting || ""}
          onChange={(e) => patch({ greeting: e.target.value })}
          placeholder="Hi {first_name},"
          disabled={!isAdmin}
          className="bg-white border-slate-200"
        />
      </div>

      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Body</Label>
        <Textarea
          data-testid="welcome-body"
          value={we.body || ""}
          onChange={(e) => patch({ body: e.target.value })}
          rows={6}
          placeholder="Welcome to Aurora — we're really glad to have you with us..."
          disabled={!isAdmin}
          className="bg-white border-slate-200"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Useful links</Label>
          {isAdmin && (
            <Button
              data-testid="welcome-add-link"
              variant="outline" size="sm"
              onClick={addLink}
              className="h-8 border-slate-200"
            >
              <Plus size={14} weight="bold" /> Add link
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {(we.links || []).map((l, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`welcome-link-row-${idx}`}>
              <Input
                data-testid={`welcome-link-title-${idx}`}
                value={l.title || ""}
                onChange={(e) => patchLink(idx, { title: e.target.value })}
                placeholder="Starter handbook"
                disabled={!isAdmin}
                className="bg-white border-slate-200 flex-1"
              />
              <Input
                data-testid={`welcome-link-url-${idx}`}
                value={l.url || ""}
                onChange={(e) => patchLink(idx, { url: e.target.value })}
                placeholder="https://..."
                disabled={!isAdmin}
                className="bg-white border-slate-200 flex-1"
              />
              {isAdmin && (
                <>
                  <Button variant="ghost" size="icon" className="h-8 w-8"
                    onClick={() => moveLink(idx, -1)} disabled={idx === 0}
                    data-testid={`welcome-link-up-${idx}`}>
                    <ArrowUp size={14} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8"
                    onClick={() => moveLink(idx, 1)} disabled={idx === (we.links || []).length - 1}
                    data-testid={`welcome-link-down-${idx}`}>
                    <ArrowDown size={14} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500"
                    onClick={() => removeLink(idx)}
                    data-testid={`welcome-link-remove-${idx}`}>
                    <Trash size={14} />
                  </Button>
                </>
              )}
            </div>
          ))}
          {(we.links || []).length === 0 && (
            <div className="text-[11px] text-slate-400">No links yet — add starter handbook, support portal, benefits, etc.</div>
          )}
        </div>
      </div>

      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Signature</Label>
        <Textarea
          data-testid="welcome-signature"
          value={we.signature || ""}
          onChange={(e) => patch({ signature: e.target.value })}
          rows={2}
          placeholder="— The Aurora People Team"
          disabled={!isAdmin}
          className="bg-white border-slate-200"
        />
      </div>

      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Email header logo</Label>
        <div className="flex items-center gap-3 mt-2">
          {we.logo_url ? (
            <img
              data-testid="welcome-logo-preview"
              src={we.logo_url} alt="Welcome logo"
              style={{ maxHeight: 56, maxWidth: 180, objectFit: "contain" }}
              className="rounded bg-slate-50 border border-slate-200 p-1"
            />
          ) : (
            <div className="h-14 w-28 rounded border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
              <ImageIcon size={20} />
            </div>
          )}
          {isAdmin && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
                data-testid="welcome-logo-input"
              />
              <Button
                variant="outline" size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="welcome-logo-upload"
                className="h-9 border-slate-200"
              >
                {uploading ? "Uploading…" : (we.logo_url ? "Replace" : "Upload")}
              </Button>
              {we.logo_url && (
                <Button
                  variant="ghost" size="sm" onClick={clearLogo}
                  className="h-9 text-red-500"
                  data-testid="welcome-logo-remove"
                >
                  Remove
                </Button>
              )}
            </>
          )}
        </div>
        <div className="text-[11px] text-slate-500 mt-1">
          PNG, JPG, GIF, WEBP or SVG. 1 MB max. Shown at the top of the welcome email.
        </div>
      </div>

      <div className="flex justify-end pt-2 border-t border-slate-100">
        <Button
          variant="outline" size="sm"
          onClick={openPreview}
          data-testid="welcome-preview"
          className="h-9 border-slate-200"
        >
          Preview
        </Button>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="welcome-preview-dialog">
          <DialogHeader>
            <DialogTitle>Welcome email preview</DialogTitle>
          </DialogHeader>
          {preview && (
            <>
              <div className="text-[11px] font-mono text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
                <div><b>Subject:</b> {preview.subject}</div>
                <div><b>Sample recipient:</b> {preview.sample_recipient}</div>
              </div>
              <div className="border border-slate-200 rounded overflow-hidden" style={{ maxHeight: 500, overflowY: "auto" }}>
                <iframe
                  title="welcome preview"
                  data-testid="welcome-preview-iframe"
                  srcDoc={preview.body_html}
                  style={{ width: "100%", height: 480, border: 0 }}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
